import { useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import { ThemeToggle } from '@/components/layout/ThemeToggle'
import { ErrorBoundary } from '@/components/layout/ErrorBoundary'
import { Input } from '@brent/core-ui/ui'
import { SearchIcon, Menu, X } from 'lucide-react'

const navigation = [
  { name: 'Dashboard', href: '/' },
  { name: 'Composers', href: '/composers' },
  { name: 'Works', href: '/works' },
  { name: 'Performers', href: '/performers' },
  { name: 'Concerts', href: '/concerts' },
  { name: 'Venues', href: '/venues' },
]

export function Layout() {
  const { user, isAuthenticated, signIn, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchValue, setSearchValue] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = searchValue.trim()
    if (trimmed.length >= 2) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`)
      setSearchValue('')
      setMobileMenuOpen(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/images/BSO%20Icon.svg" alt="BSO Logo" className="h-8 dark:invert" />
              <span className="text-lg font-bold">Concert Archive</span>
            </Link>
            <nav className="hidden lg:flex space-x-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'text-sm font-medium transition-colors hover:text-primary',
                    location.pathname === item.href ||
                      (item.href !== '/' && location.pathname.startsWith(item.href))
                      ? 'text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center space-x-3">
            {/* Desktop search */}
            <form onSubmit={handleSearch} className="hidden md:block">
              <div className="relative">
                <SearchIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="h-8 w-40 pl-8 text-sm lg:w-56"
                />
              </div>
            </form>
            <ThemeToggle />
            {isAuthenticated ? (
              <>
                <span className="hidden sm:inline text-sm text-muted-foreground">
                  {user?.email}
                </span>
                <button
                  onClick={signOut}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                onClick={signIn}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Sign in
              </button>
            )}
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-1 text-muted-foreground hover:text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t">
            <div className="container mx-auto px-4 py-3 space-y-3">
              {/* Mobile search */}
              <form onSubmit={handleSearch} className="md:hidden">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </form>
              <nav className="flex flex-col space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      'rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
                      location.pathname === item.href ||
                        (item.href !== '/' && location.pathname.startsWith(item.href))
                        ? 'text-foreground bg-accent'
                        : 'text-muted-foreground',
                    )}
                  >
                    {item.name}
                  </Link>
                ))}
                <Link
                  to="/search"
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
                    location.pathname === '/search'
                      ? 'text-foreground bg-accent'
                      : 'text-muted-foreground',
                  )}
                >
                  Search
                </Link>
              </nav>
            </div>
          </div>
        )}
      </header>
      <main className="container mx-auto px-4 py-8">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
      <footer className="border-t py-6 mt-auto">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Brent Symphony Orchestra. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
